import {
  type ComponentPropsWithoutRef,
  type ElementType,
  memo,
  useMemo,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import OpenSideIcon from '@/assets/icons/OpenSideIcon';

import { CenterFlex, Flex } from '../base';
import Checkbox from '../Checkbox/Checkbox';
import { MainText } from '../Typography/NamedTypo';
import DataRowDataItem from './DataRowDataItem';

type Props = ComponentPropsWithoutRef<ElementType> & {
  name: string;
  selected: boolean;
  parentId: string;
  row: DataTableRowT;
  domColumnConfigs: ColumnConfigsResT[];
  columns: DataTableColumnT[];
  checkable?: boolean;
  openButtonOffsetX?: number;
  onCheck?: (rowKey: string, status: boolean) => void;
  checkedList: string[];
  visible?: boolean;
};

const DataRowItem = (props: Props) => {
  const ref = useRef(null);

  const defaultColumns = useMemo(() => {
    return props.columns.filter((col) => col.isDefaultColumn);
  }, [props.columns]);

  const defaultColumsData = useMemo(() => {
    return props.row.rowData.filter((data) => {
      const col = props.columns.find(
        (column) => column.columnKey === data.columnKey,
      );
      if (!col) return false;
      return col?.visible && col?.isDefaultColumn;
    });
  }, [props.columns, props.row.rowData]);

  const customColumnsData = useMemo(() => {
    return props.row.rowData
      .filter((data) => {
        const col = props.columns.find(
          (column) => column.columnKey === data.columnKey,
        );
        if (!col) return false;
        return col?.visible && !col?.isDefaultColumn;
      })
      .sort((a, b) => {
        const aConfig = props.domColumnConfigs.find(
          (config) => config.name === a.columnKey,
        );
        const bConfig = props.domColumnConfigs.find(
          (config) => config.name === b.columnKey,
        );
        return (aConfig?.order || 0) - (bConfig?.order || 0);
      });
  }, [props.columns, props.domColumnConfigs, props.row.rowData]);

  return (
    <Style
      hasOnClickHandler={!!props.row.onRowClick}
      selected={props.selected}
      ref={ref}
      style={{ ...props.row.rowStyle, ...props.style }}
      onContextMenu={(e) => props.row.onRowRightClick?.(props.row.rowKey, e)}
    >
      {props.row.onRowClick && (
        <FloatingButtonWrapper>
          <FloatingOpenButton
            g="4px"
            offsetX={props.openButtonOffsetX}
            className="btn-row-content-open"
            onClick={(e) => {
              props.row.onRowClick?.(props.row.rowKey, e);
            }}
          >
            <OpenSideIcon />
            <MainText>Open</MainText>
          </FloatingOpenButton>
        </FloatingButtonWrapper>
      )}
      {props.checkable && (
        <CheckboxTd className="data-row-data-item">
          <Checkbox
            checked={
              !!props.checkedList.find(
                (key) => key === props.row.rowKey && !props.visible,
              )
            }
            onChange={(status) => {
              props.onCheck?.(props.row.rowKey, status);
            }}
            style={{
              visibility:
                props.visible === undefined
                  ? 'visible'
                  : props.visible
                    ? 'visible'
                    : 'hidden',
            }}
          />
        </CheckboxTd>
      )}
      {defaultColumsData.map((data, index) => {
        const col = props.columns.find(
          (column) => column.columnKey === data.columnKey,
        );

        const accumulatedWidth = defaultColumns.reduce(
          (acc, curr, idx) => {
            if (idx >= index) return acc;
            const width = defaultColumns[idx].width || 0;
            return acc + width;
          },
          props.checkable ? 40 : 0,
        );

        return (
          <DataRowDataItem
            key={data.columnKey}
            {...data}
            rowKey={props.rowKey}
            width={col?.width}
            style={{ ...col?.columnStyle, flex: col?.responsive ? 1 : 'unset' }}
            fixed={col?.isDefaultColumn}
            offset={accumulatedWidth}
            isLastFixedIndex={
              customColumnsData.length > 0 &&
              index === defaultColumns.length - 1
            }
          />
        );
      })}
      {customColumnsData.map((data) => {
        const col = props.columns.find(
          (column) => column.columnKey === data.columnKey,
        );
        return (
          <DataRowDataItem
            key={data.columnKey}
            {...data}
            width={col?.width}
            style={{ flex: col?.responsive ? 1 : 'unset' }}
            rowKey={props.rowKey}
          />
        );
      })}
    </Style>
  );
};

type StyleProps = { hasOnClickHandler: boolean };

const Style = styled(Flex).attrs({
  a: 'center',
})<StyleProps>`
  position: relative;
  height: 52px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray250};
  background-color: ${(props) => props.theme.colors.gray100};
  transition:
    background-color 0.3s,
    filter 0.2s;
  .empty {
    height: 52px;
  }
  .btn-row-content-open {
    opacity: 0;
  }
  .data-row-data-item {
    transition: all 0.2s;
  }
  &:hover {
    .btn-row-content-open {
      opacity: 1;
    }
    .data-row-data-item {
      background-color: ${(props) => props.theme.colors.hover};
    }
  }

  ${({ hasOnClickHandler }) => hasOnClickHandler && css``}
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${(props) => props.theme.colors.selected};
    `}
`;

const FloatingButtonWrapper = styled(Flex).attrs({ a: 'center' })`
  z-index: 2;
  position: sticky;
  left: 0;
  width: 0;
  height: 100%;
`;

const FloatingOpenButton = styled(Flex)<{ offsetX?: number }>`
  z-index: 2;
  position: absolute;
  left: ${(props) => (props.offsetX ? props.offsetX + 8 : 8)}px;
  background-color: ${(props) => props.theme.colors.gray200};
  padding: 4px 8px;
  box-shadow: 0px 2px 8px 0px ${(props) => props.theme.colors.FD08};
  border-radius: 4px;
  cursor: pointer;
`;

const CheckboxTd = styled(CenterFlex)`
  width: 40px;
  flex-shrink: 0;
  position: sticky;
  left: 0px;
  z-index: 1;
  height: 100%;
  background-color: ${(props) => props.theme.colors.gray100};
`;

export const DataTableRow = styled(Flex)``;

export default memo(DataRowItem);
